import { FC } from 'react'
import { AlertCircle, CheckCircle2, Layers, AlertTriangle, Wrench, Clock } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta, PmsDerivedKpis } from './types'
import type { PmsActionCenterDto } from '../../../core/pms/_models'

interface Props {
  loading: boolean
  kpis: PmsDerivedKpis
  actionCenter: PmsActionCenterDto | null
  onKpiClick: (kpi: string) => void
}

const kpiMeta: Record<string, CardMeta> = {
  overdueJobs: {
    description: 'Total number of jobs past their due date requiring immediate attention.',
    endpoint: 'GET /pms/dashboard/command-center',
    logic: 'Displays overdueJobs from Command Center stats, falls back to overdue jobs array length.',
  },
  closedJobs: {
    description: 'Jobs completed and closed in the last 30 days.',
    endpoint: 'GET /pms/dashboard/command-center',
    logic: 'Displays closedLast30Days field from Command Center stats.',
  },
  totalJobs: {
    description: 'Total number of PMS jobs across all states.',
    endpoint: 'GET /pms/dashboard/command-center',
    logic: 'Displays totalJobs field from Command Center stats.',
  },
  unplannedOverdue: {
    description: 'Overdue jobs originating from breakdowns or defect requests.',
    endpoint: 'GET /pms/dashboard/command-center',
    logic: 'Displays unplannedOverdueJobs field from Command Center stats.',
  },
  pendingDefectRequests: {
    description: 'Defect requests awaiting review and job creation.',
    endpoint: 'GET /pms/dashboard/action-center',
    logic: 'Displays pendingDefectRequests field from Action Center stats.',
  },
  pendingPostponements: {
    description: 'Postponement requests pending approval.',
    endpoint: 'GET /pms/dashboard/action-center',
    logic: 'Displays pendingPostponements field from Action Center stats.',
  },
}

const KpiCards: FC<Props> = ({ loading, kpis, actionCenter, onKpiClick }) => {
  const sparkPath = (color: string) => (
    <svg className='w-full h-full' viewBox='0 0 100 30' preserveAspectRatio='none'>
      <polyline fill='none' stroke={color} strokeWidth='1.5' points='0,22 15,20 30,18 45,15 60,14 75,12 90,10 100,8' />
    </svg>
  )

  return (
    <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
      {/* Overdue Jobs */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('overdueJobs')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Overdue Jobs</div>
          <CardInfoButton meta={kpiMeta.overdueJobs} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>
          {loading ? <span className='inline-block w-12 h-6 bg-slate-200 rounded animate-pulse my-1' /> : kpis.overdueJobs}
        </div>
        <div className='text-[10px] text-red-500 flex items-center gap-0.5 mt-1'>
          <AlertCircle className='w-3 h-3' />Requires attention
        </div>
        <div className='mt-2 h-10 bg-red-50 rounded-lg relative overflow-hidden'>
          <svg className='w-full h-full' viewBox='0 0 100 30' preserveAspectRatio='none'>
            <polyline fill='none' stroke='#ef4444' strokeWidth='1.5' points='0,25 15,20 30,22 45,15 60,18 75,10 90,12 100,5' />
          </svg>
        </div>
      </div>

      {/* Closed (30 Days) */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('closedJobs')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Closed (30 Days)</div>
          <CardInfoButton meta={kpiMeta.closedJobs} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>
          {loading ? <span className='inline-block w-12 h-6 bg-slate-200 rounded animate-pulse my-1' /> : kpis.closedJobs}
        </div>
        <div className='text-[10px] text-emerald-500 flex items-center gap-0.5 mt-1'>
          <CheckCircle2 className='w-3 h-3' />On track
        </div>
        <div className='mt-2 h-10 bg-emerald-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#10b981')}
        </div>
      </div>

      {/* Total Jobs */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('totalJobs')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Total Jobs</div>
          <CardInfoButton meta={kpiMeta.totalJobs} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>
          {loading ? <span className='inline-block w-12 h-6 bg-slate-200 rounded animate-pulse my-1' /> : kpis.totalJobs}
        </div>
        <div className='text-[10px] text-blue-500 flex items-center gap-0.5 mt-1'>
          <Layers className='w-3 h-3' />All states
        </div>
        <div className='mt-2 h-10 bg-blue-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#3b82f6')}
        </div>
      </div>

      {/* Unplanned Overdue */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('unplannedOverdue')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Unplanned Overdue</div>
          <CardInfoButton meta={kpiMeta.unplannedOverdue} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>
          {loading ? <span className='inline-block w-12 h-6 bg-slate-200 rounded animate-pulse my-1' /> : kpis.unplannedOverdue}
        </div>
        <div className='text-[10px] text-amber-500 flex items-center gap-0.5 mt-1'>
          <AlertTriangle className='w-3 h-3' />Breakdown / Defect
        </div>
        <div className='mt-2 h-10 bg-amber-50 rounded-lg relative overflow-hidden flex items-end justify-around px-1'>
          {[40, 60, 45, 70, 55, 80, 65, 90].map((h, i) => (
            <div key={i} className='w-1.5 bg-amber-400 rounded-t' style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>

      {/* Pending Defect Requests */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('pendingDefectRequests')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Pending Defect Requests</div>
          <CardInfoButton meta={kpiMeta.pendingDefectRequests} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>
          {loading ? <span className='inline-block w-12 h-6 bg-slate-200 rounded animate-pulse my-1' /> : kpis.pendingDefectRequests}
        </div>
        <div className='text-[10px] text-rose-500 flex items-center gap-0.5 mt-1'>
          <Wrench className='w-3 h-3' />Awaiting review
        </div>
        <div className='mt-2 h-10 bg-rose-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#f43f5e')}
        </div>
      </div>

      {/* Pending Postponements */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('pendingPostponements')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Pending Postponements</div>
          <CardInfoButton meta={kpiMeta.pendingPostponements} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>
          {loading ? <span className='inline-block w-12 h-6 bg-slate-200 rounded animate-pulse my-1' /> : kpis.pendingPostponements}
        </div>
        <div className='text-[10px] text-indigo-500 flex items-center gap-0.5 mt-1'>
          <Clock className='w-3 h-3' />Needs approval
        </div>
        <div className='mt-2 h-10 bg-indigo-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#6366f1')}
        </div>
      </div>
    </div>
  )
}

export default KpiCards
